// @flow
import { Box, Button, CircularProgress, Container, CssBaseline, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import routes from '../routes';
import Copyright from '../component/Copyright';
import { AuthContext } from '../context/auth';
import { isNotValidEmail } from '../util/validator';
import ApiResponse from '../model/ApiResponse';

const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
  },
  success: {
    color: 'white',
    backgroundColor: theme.palette.success[300],
    textAlign: 'justify',
    padding: theme.spacing(1),
    borderColor: theme.palette.success[900],
    borderStyle: 'solid',
    marginTop: theme.spacing(2),
  },
}));

const RecoverPassword = () => {
  const { t } = useTranslation();
  const { doPost } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [success, setSuccess] = useState<?string>();

  const validate = values => {
    const errors = {};
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const response: ApiResponse = await doPost('/user/send-recover', values);
    if (response.ok) {
      const msg = 'We sent you an email with recovery instructions';
      setSuccess(msg);
      enqueueSnackbar(t(msg), {
        variant: 'success',
      });
    } else {
      enqueueSnackbar(t(response.message), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline/>
      <div className={classes.paper}>
        <img src={`${baseUrl}/images/logo.png`} alt="" width={40} height={40}/>
        <Typography component="h1" variant="h5">
          {t('Recover password')}
        </Typography>
        <Formik
          initialValues={{
            email: '',
          }}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={t('Email')}
                    name="email"
                    autoComplete="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  <span className={classes.error}>{errors.email && touched.email && errors.email}</span>
                </Grid>
              </Grid>
              {!!success && <Typography className={classes.success} variant="h6" gutterBottom>
                {t(success)}
              </Typography>}
              {!success && <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress color="secondary" size="1.2em"/> : null}
              >
                {t('Send recovery email')}
              </Button>}
              {!success && <Grid container justify="flex-end">
                <Grid item>
                  {!isSubmitting && <Link to={routes.signUp}>
                    {t('Don\'t have an account? Sign Up')}
                  </Link>}
                </Grid>
              </Grid>}
            </form>
          )}
        </Formik>
      </div>
      <Box mt={8}>
        <Copyright/>
      </Box>
    </Container>
  );
};

export default RecoverPassword;
